'use client';

import React from 'react';
import {
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import Link from 'next/link';

type ScheduleHealthPayload = {
  upcomingIn24h: { id: string; scheduledFor: string; title: string; contentId: string }[];
  upcomingCount: number;
  connectionsNeedingAttention: { platform: string; reason: 'expired' | 'expiring_soon' }[];
};

/**
 * Hub 1.3 helper: surface scheduled windows + OAuth health so users can reconnect before go-live.
 */
export function ScheduleHealthCard() {
  const [data, setData] = React.useState<ScheduleHealthPayload | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch('/api/hub/schedule-health', { credentials: 'include' });
        const j = (await r.json().catch(() => ({}))) as ScheduleHealthPayload & { error?: string };
        if (!r.ok) {
          if (!cancelled) setErr(j.error ?? 'Could not load schedule health.');
          return;
        }
        if (!cancelled) {
          setData({
            upcomingIn24h: j.upcomingIn24h ?? [],
            upcomingCount: j.upcomingCount ?? 0,
            connectionsNeedingAttention: j.connectionsNeedingAttention ?? [],
          });
        }
      } catch {
        if (!cancelled) setErr('Could not load schedule health.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const showWarning =
    Boolean(data?.upcomingCount) && (data?.connectionsNeedingAttention?.length ?? 0) > 0;

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Schedule & connections (next 24h)
        </Typography>
        {err ? (
          <Typography variant="body2" color="error">
            {err}
          </Typography>
        ) : !data ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {data.upcomingCount === 0
                ? 'No posts scheduled in the next 24 hours.'
                : `${data.upcomingCount} scheduled post(s) in the next 24 hours.`}{' '}
              Reconnect any expired accounts under Connected Accounts so publishing stays reliable.
            </Typography>
            {showWarning ? (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
                You have upcoming scheduled content and at least one connection needs attention.
              </Alert>
            ) : null}
            {data.connectionsNeedingAttention.length > 0 ? (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                {data.connectionsNeedingAttention.map((c, i) => (
                  <Chip
                    key={`${c.platform}-${c.reason}-${i}`}
                    size="small"
                    color={c.reason === 'expired' ? 'error' : 'warning'}
                    label={`${c.platform}: ${c.reason === 'expired' ? 'expired' : 'expiring soon'}`}
                  />
                ))}
              </Stack>
            ) : null}
            {data.upcomingIn24h.length > 0 ? (
              <Typography component="div" variant="body2">
                Next:{' '}
                <strong>{data.upcomingIn24h[0]?.title || 'Untitled'}</strong> at{' '}
                {data.upcomingIn24h[0]
                  ? new Date(data.upcomingIn24h[0].scheduledFor).toLocaleString(undefined, {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })
                  : ''}
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              <Link href="/dashboard/profile?section=integrations">Manage connections</Link>
              {' · '}
              <Link href="/dashboard/calendar?view=schedule">Publishing queue (Calendar Schedule view)</Link>
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
